import React, { useState, useEffect } from 'react';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
} from 'recharts';
import {
  COUNTRY_OPTIONS,
  CITY_OPTIONS_BY_COUNTRY,
  LANGUAGE_OPTIONS,
} from '../../constants/audienceOptions';
import { isEmptyValue, hasAnyArrayData, hasAnyData } from '../../utils/profileVisibility';
import SectionEditModal from './SectionEditModal';
import EditIconButton from './EditIconButton';

function AudienceSection({ audience, isOwner, onSaveAudience, mode = 'edit' }) {
  const safeAudience = audience || {};
  
  // Debug logging
  useEffect(() => {
    console.log('AudienceSection - Received audience prop:', audience);
    console.log('AudienceSection - safeAudience:', safeAudience);
    console.log('AudienceSection - Has data?', Object.keys(safeAudience).length > 0);
  }, [audience, safeAudience]);

  const initialGender = safeAudience.gender || {};
  const initialAge = safeAudience.ageBrackets || {};

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  const [formState, setFormState] = useState({
    femalePercent: initialGender.femalePercent ?? '',
    malePercent: initialGender.malePercent ?? '',
    otherPercent: initialGender.otherPercent ?? '',
    age13_17: initialAge['13-17'] ?? '',
    age18_24: initialAge['18-24'] ?? '',
    age25_34: initialAge['25-34'] ?? '',
    age35_plus: initialAge['35+'] ?? '',
    country1Name: safeAudience.topCountries?.[0]?.name || '',
    country2Name: safeAudience.topCountries?.[1]?.name || '',
    country3Name: safeAudience.topCountries?.[2]?.name || '',
    city1Name: safeAudience.topCities?.[0]?.name || '',
    city2Name: safeAudience.topCities?.[1]?.name || '',
    primaryLanguage: safeAudience.primaryLanguage || '',
  });

  useEffect(() => {
    const gender = audience?.gender || {};
    const ages = audience?.ageBrackets || {};
    setFormState((prev) => ({
      ...prev,
      femalePercent: gender.femalePercent ?? '',
      malePercent: gender.malePercent ?? '',
      otherPercent: gender.otherPercent ?? '',
      age13_17: ages['13-17'] ?? '',
      age18_24: ages['18-24'] ?? '',
      age25_34: ages['25-34'] ?? '',
      age35_plus: ages['35+'] ?? '',
      country1Name: audience?.topCountries?.[0]?.name || '',
      country2Name: audience?.topCountries?.[1]?.name || '',
      country3Name: audience?.topCountries?.[2]?.name || '',
      city1Name: audience?.topCities?.[0]?.name || '',
      city2Name: audience?.topCities?.[1]?.name || '',
      primaryLanguage: audience?.primaryLanguage || '',
    }));
  }, [audience]);

  const parsePercent = (value) => {
    if (value === '' || value === null || typeof value === 'undefined') return undefined;
    const num = Number(value);
    if (Number.isNaN(num) || num < 0 || num > 100) return 'invalid';
    return num;
  };

  const handleSave = async () => {
    setError(null);

    // validate percents (light)
    const genderFields = [
      ['femalePercent', formState.femalePercent],
      ['malePercent', formState.malePercent],
      ['otherPercent', formState.otherPercent],
    ];
    const ageFields = [
      ['age13_17', formState.age13_17],
      ['age18_24', formState.age18_24],
      ['age25_34', formState.age25_34],
      ['age35_plus', formState.age35_plus],
    ];
    const percentFields = [...genderFields, ...ageFields];

    for (const [field, val] of percentFields) {
      if (val === '' || val === null || typeof val === 'undefined') continue;
      const num = Number(val);
      if (Number.isNaN(num) || num < 0 || num > 100) {
        setError('All percentage values must be between 0 and 100, or left blank.');
        return;
      }
    }

    // Build gender object, only including fields that have values
    const gender = {};
    if (formState.femalePercent !== '') {
      gender.femalePercent = Number(formState.femalePercent);
    }
    if (formState.malePercent !== '') {
      gender.malePercent = Number(formState.malePercent);
    }
    if (formState.otherPercent !== '') {
      gender.otherPercent = Number(formState.otherPercent);
    }

    // Build ageBrackets object, only including fields that have values
    const ageBrackets = {};
    if (formState.age13_17 !== '') {
      ageBrackets['13-17'] = Number(formState.age13_17);
    }
    if (formState.age18_24 !== '') {
      ageBrackets['18-24'] = Number(formState.age18_24);
    }
    if (formState.age25_34 !== '') {
      ageBrackets['25-34'] = Number(formState.age25_34);
    }
    if (formState.age35_plus !== '') {
      ageBrackets['35+'] = Number(formState.age35_plus);
    }

    const topCountries = [
      formState.country1Name.trim(),
      formState.country2Name.trim(),
      formState.country3Name.trim(),
    ]
      .filter((name) => name)
      .map((name) => ({ name }));

    const topCities = [
      formState.city1Name.trim(),
      formState.city2Name.trim(),
    ]
      .filter((name) => name)
      .map((name) => ({ name }));

    // Build payload, only including fields that have values
    const payload = {};
    
    // Only include gender if it has at least one field
    if (Object.keys(gender).length > 0) {
      payload.gender = gender;
    }
    
    // Only include ageBrackets if it has at least one field
    if (Object.keys(ageBrackets).length > 0) {
      payload.ageBrackets = ageBrackets;
    }
    
    // Only include topCountries if it has entries
    if (topCountries.length > 0) {
      payload.topCountries = topCountries;
    }
    
    // Only include topCities if it has entries
    if (topCities.length > 0) {
      payload.topCities = topCities;
    }
    
    // Only include primaryLanguage if it has a value
    if (formState.primaryLanguage.trim()) {
      payload.primaryLanguage = formState.primaryLanguage.trim();
    }

    try {
      setIsSaving(true);
      console.log('Saving audience payload:', payload);
      await onSaveAudience(payload);
      console.log('Audience saved successfully');
      setIsModalOpen(false);
      setError(null);
    } catch (e) {
      console.error('Error saving audience:', e);
      setError(`Failed to save audience: ${e.message || e}. Please try again.`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setError(null);
    // Reset form from audience
    const gender = audience?.gender || {};
    const ages = audience?.ageBrackets || {};
    setFormState({
      femalePercent: gender.femalePercent ?? '',
      malePercent: gender.malePercent ?? '',
      otherPercent: gender.otherPercent ?? '',
      age13_17: ages['13-17'] ?? '',
      age18_24: ages['18-24'] ?? '',
      age25_34: ages['25-34'] ?? '',
      age35_plus: ages['35+'] ?? '',
      country1Name: audience?.topCountries?.[0]?.name || '',
      country2Name: audience?.topCountries?.[1]?.name || '',
      country3Name: audience?.topCountries?.[2]?.name || '',
      city1Name: audience?.topCities?.[0]?.name || '',
      city2Name: audience?.topCities?.[1]?.name || '',
      primaryLanguage: audience?.primaryLanguage || '',
    });
  };

  // VIEW MODE
  const renderViewMode = () => {
    const gender = safeAudience.gender || {};
    const ages = safeAudience.ageBrackets || {};
    const topCountries = safeAudience.topCountries || [];
    const topCities = safeAudience.topCities || [];

    // Ranked lists for top countries and cities (used in public + edit view mode)
    const rankedCountries = (topCountries || [])
      .filter((item) => item && item.name)
      .slice(0, 3);

    const rankedCities = (topCities || [])
      .filter((item) => item && item.name)
      .slice(0, 3);

    // Build chart data
    const genderData = [
      { key: 'women', label: 'Women', value: gender.femalePercent || 0 },
      { key: 'men', label: 'Men', value: gender.malePercent || 0 },
      { key: 'other', label: 'Other', value: gender.otherPercent || 0 },
    ].filter((item) => item.value > 0);

    const ageData = [
      { key: '13-17', label: '13–17', value: ages['13-17'] || 0 },
      { key: '18-24', label: '18–24', value: ages['18-24'] || 0 },
      { key: '25-34', label: '25–34', value: ages['25-34'] || 0 },
      { key: '35+', label: '35+', value: ages['35+'] || 0 },
    ].filter((item) => item.value > 0);

    // Check if there's any meaningful data
    const hasCountries = rankedCountries.length > 0;
    const hasAges = ageData.length > 0;
    const hasGender = genderData.length > 0;
    const hasLanguage = !isEmptyValue(safeAudience.primaryLanguage);

    const hasAnyData =
      hasGender ||
      hasAges ||
      hasCountries ||
      hasLanguage;

    // Hide section if empty in public mode
    if (mode === 'public' && !hasAnyData) {
      return null;
    }

    const GENDER_COLORS = ['#009F90', '#7842D6', '#9CA3AF']; // teal, purple, gray
    const AGE_BAR_COLOR = '#009F90'; // teal

    return (
      <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 md:p-6 mb-5 md:mb-6 relative">
        <div className="mb-3 md:mb-4 flex items-center justify-between gap-2">
          <h2 className="text-lg md:text-xl font-semibold text-slate-900">
            Audience & Demographics
          </h2>
        </div>
        {isOwner && (
          <div className="absolute right-4 sm:right-6 top-4 sm:top-6 z-10">
            <EditIconButton onClick={() => setIsModalOpen(true)} />
          </div>
        )}

        {!hasAnyData ? (
          <p className="text-sm text-slate-500">
            No audience data added yet. Add your gender, age, and location
            breakdown to help brands understand who you reach.
          </p>
        ) : (
          <div className="space-y-6">
            {/* Top row: charts */}
            <div className={`grid gap-6 ${(mode === 'public' && ageData.length === 0) ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
              {/* Gender pie */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">
                  Gender
                </h3>
                {genderData.length === 0 ? (
                  <p className="text-xs text-slate-500">
                    No gender data provided.
                  </p>
                ) : (
                  <div className="flex items-center gap-4">
                    <div className="h-32 w-32">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={genderData}
                            dataKey="value"
                            innerRadius={32}
                            outerRadius={48}
                            paddingAngle={2}
                            stroke="#FFFFFF"
                          >
                            {genderData.map((entry, index) => (
                              <Cell
                                key={entry.key}
                                fill={
                                  GENDER_COLORS[index % GENDER_COLORS.length]
                                }
                              />
                            ))}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="space-y-2">
                      {genderData.map((item, index) => (
                        <div key={item.key} className="flex items-center justify-between gap-2 text-xs text-slate-700">
                          <div className="flex items-center gap-2">
                            <span
                              className="inline-flex h-2.5 w-2.5 rounded-full"
                              style={{
                                backgroundColor:
                                  GENDER_COLORS[index % GENDER_COLORS.length],
                              }}
                            />
                            <span>{item.label}</span>
                          </div>
                          <span className="font-medium">
                            {item.value.toFixed(0)}%
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Age bar chart - only show if there's data in public mode */}
              {(mode !== 'public' || ageData.length > 0) && (
                <div className="bg-slate-50 rounded-lg p-4">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">
                    Age
                  </h3>
                  {ageData.length === 0 ? (
                    <p className="text-xs text-slate-500">
                      No age breakdown provided.
                    </p>
                  ) : (
                    <div className="h-40">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={ageData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                          <XAxis
                            dataKey="label"
                            tickLine={false}
                            axisLine={false}
                            tick={{ fontSize: 10, fill: '#111827' }}
                          />
                          <YAxis
                            tickLine={false}
                            axisLine={false}
                            tick={{ fontSize: 10, fill: '#111827' }}
                            tickFormatter={(v) => `${v}%`}
                          />
                          <Tooltip
                            cursor={{ fill: 'rgba(148,163,184,0.15)' }}
                            formatter={(value) => [`${value}%`, 'Age']}
                          />
                          <Bar
                            dataKey="value"
                            radius={[4, 4, 0, 0]}
                            fill={AGE_BAR_COLOR}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Bottom row: countries + cities + language */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Top countries - ranked list */}
              {rankedCountries.length > 0 ? (
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">
                    Top countries
                  </h3>
                  <div className="mt-1 space-y-3">
                    {rankedCountries.map((item, idx) => (
                      <div key={item.name} className="flex items-center gap-3">
                        <div className="flex items-center justify-center h-6 w-6 rounded-full bg-slate-100 text-gray-700 text-xs font-semibold">
                          {idx + 1}
                        </div>
                        <span className="text-sm text-gray-900">{item.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {/* Language + Top Cities */}
              {(mode !== 'public' || !isEmptyValue(safeAudience.primaryLanguage) || rankedCities.length > 0) && (
                <div className="space-y-6">
                  {/* Primary Language - only show if there's data in public mode */}
                  {(mode !== 'public' || !isEmptyValue(safeAudience.primaryLanguage)) && (
                    <div>
                      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                        Primary audience language
                      </h3>
                      <p className="text-sm text-slate-700">
                        {safeAudience.primaryLanguage || 'Not specified'}
                      </p>
                    </div>
                  )}

                  {/* Top Cities - ranked list */}
                  {rankedCities.length > 0 ? (
                    <div>
                      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">
                        Top cities
                      </h3>
                      <div className="mt-1 space-y-3">
                        {rankedCities.map((item, idx) => (
                          <div key={item.name} className="flex items-center gap-3">
                            <div className="flex items-center justify-center h-6 w-6 rounded-full bg-slate-100 text-gray-700 text-xs font-semibold">
                              {idx + 1}
                            </div>
                            <span className="text-sm text-gray-900">{item.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        )}
      </section>
    );
  };

  // EDIT MODE - Render form content for modal
  const renderEditForm = () => {
    // Derive city options based on selected countries
    const city1Country = formState.country1Name || '';
    const city2Country = formState.country2Name || '';

    const city1Options = CITY_OPTIONS_BY_COUNTRY[city1Country] || [];
    const city2Options = CITY_OPTIONS_BY_COUNTRY[city2Country] || [];

    return (
      <div className="space-y-4">
        {/* Gender */}
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
            Gender (%)
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600">Women</label>
              <input
                type="number"
                min={0}
                max={100}
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900"
                value={formState.femalePercent}
                onChange={(e) =>
                  setFormState((prev) => ({ ...prev, femalePercent: e.target.value }))
                }
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Men</label>
              <input
                type="number"
                min={0}
                max={100}
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900"
                value={formState.malePercent}
                onChange={(e) =>
                  setFormState((prev) => ({ ...prev, malePercent: e.target.value }))
                }
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Other</label>
              <input
                type="number"
                min={0}
                max={100}
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900"
                value={formState.otherPercent}
                onChange={(e) =>
                  setFormState((prev) => ({ ...prev, otherPercent: e.target.value }))
                }
              />
            </div>
          </div>
        </div>

        {/* Age */}
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
            Age (%)
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600">13–17</label>
              <input
                type="number"
                min={0}
                max={100}
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900"
                value={formState.age13_17}
                onChange={(e) =>
                  setFormState((prev) => ({ ...prev, age13_17: e.target.value }))
                }
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">18–24</label>
              <input
                type="number"
                min={0}
                max={100}
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900"
                value={formState.age18_24}
                onChange={(e) =>
                  setFormState((prev) => ({ ...prev, age18_24: e.target.value }))
                }
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">25–34</label>
              <input
                type="number"
                min={0}
                max={100}
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900"
                value={formState.age25_34}
                onChange={(e) =>
                  setFormState((prev) => ({ ...prev, age25_34: e.target.value }))
                }
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">35+</label>
              <input
                type="number"
                min={0}
                max={100}
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900"
                value={formState.age35_plus}
                onChange={(e) =>
                  setFormState((prev) => ({ ...prev, age35_plus: e.target.value }))
                }
              />
            </div>
          </div>
        </div>

        {/* Top countries */}
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
            Top countries
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[1, 2, 3].map((idx) => {
              const nameKey = `country${idx}Name`;
              return (
                <div key={idx}>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Country {idx}
                  </label>
                  <select
                    className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-1 focus:ring-ihTeal"
                    value={formState[nameKey] || ''}
                    onChange={(e) => {
                      const newCountry = e.target.value;
                      // Reset corresponding city when country changes
                      const cityKey = `city${idx}Name`;
                      setFormState((prev) => ({
                        ...prev,
                        [nameKey]: newCountry,
                        [cityKey]: '', // Clear city when country changes
                      }));
                    }}
                  >
                    <option value="">Select a country</option>
                    {COUNTRY_OPTIONS.map((country) => (
                      <option key={country} value={country}>
                        {country}
                      </option>
                    ))}
                  </select>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top cities */}
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
            Top cities
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* City 1 */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                City 1
              </label>
              {city1Options.length > 0 ? (
                <select
                  className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-1 focus:ring-[#1B9DA2]"
                  value={formState.city1Name || ''}
                  onChange={(e) =>
                    setFormState((prev) => ({ ...prev, city1Name: e.target.value }))
                  }
                >
                  <option value="">Select a city</option>
                  {city1Options.map((city) => (
                    <option key={city} value={city}>
                      {city}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-1 focus:ring-[#1B9DA2]"
                  value={formState.city1Name || ''}
                  onChange={(e) =>
                    setFormState((prev) => ({ ...prev, city1Name: e.target.value }))
                  }
                  placeholder="City"
                />
              )}
            </div>
            {/* City 2 */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                City 2
              </label>
              {city2Options.length > 0 ? (
                <select
                  className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-1 focus:ring-[#1B9DA2]"
                  value={formState.city2Name || ''}
                  onChange={(e) =>
                    setFormState((prev) => ({ ...prev, city2Name: e.target.value }))
                  }
                >
                  <option value="">Select a city</option>
                  {city2Options.map((city) => (
                    <option key={city} value={city}>
                      {city}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-1 focus:ring-[#1B9DA2]"
                  value={formState.city2Name || ''}
                  onChange={(e) =>
                    setFormState((prev) => ({ ...prev, city2Name: e.target.value }))
                  }
                  placeholder="City"
                />
              )}
            </div>
          </div>
        </div>

        {/* Language */}
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">
            Primary audience language
          </label>
          <select
            className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-1 focus:ring-ihTeal"
            value={formState.primaryLanguage || ''}
            onChange={(e) =>
              setFormState((prev) => ({ ...prev, primaryLanguage: e.target.value }))
            }
          >
            <option value="">Select a language</option>
            {LANGUAGE_OPTIONS.map((lang) => (
              <option key={lang} value={lang}>
                {lang}
              </option>
            ))}
          </select>
        </div>

      </div>
    );
  };

  return (
    <>
      {renderViewMode()}
      
      {isOwner && (
        <SectionEditModal
          isOpen={isModalOpen}
          onClose={handleCancel}
          title="Edit Audience & Demographics"
          subtitle="Add the audience details brands care about most."
          onSave={handleSave}
          isSaving={isSaving}
          errors={error ? { _general: error } : {}}
        >
          {renderEditForm()}
        </SectionEditModal>
      )}
    </>
  );
}

export default AudienceSection;
